import { fetchFile } from "@ffmpeg/util";
import { loadFFmpeg } from "./videoCompression";

let fileSequence = 0;

const nextFileToken = () => {
  fileSequence += 1;
  return `${Date.now()}-${fileSequence}`;
};

const extensionForContentType = (contentType) => (
  contentType === "video/mp4" ? ".mp4" : ".webm"
);

export const cameraClipWithinLimits = ({
  blob,
  durationMs,
  maxClipBytes,
  maxClipDurationMs,
}) => (
  blob?.size > 0
  && durationMs >= 1000
  && durationMs <= maxClipDurationMs
  && blob.size <= maxClipBytes
);

export const measureVideoMetadata = (blob) => new Promise((resolve, reject) => {
  const video = document.createElement("video");
  const objectUrl = URL.createObjectURL(blob);
  const cleanup = () => {
    video.onloadedmetadata = null;
    video.onerror = null;
    video.removeAttribute("src");
    video.load();
    URL.revokeObjectURL(objectUrl);
  };

  video.preload = "metadata";
  video.onloadedmetadata = () => {
    const metadata = {
      durationMs: Math.round(video.duration * 1000),
      width: video.videoWidth,
      height: video.videoHeight,
    };
    cleanup();
    if (
      !Number.isFinite(metadata.durationMs)
      || metadata.durationMs <= 0
      || metadata.width <= 0
      || metadata.height <= 0
    ) {
      reject(new Error("Could not measure the processed camera clip."));
      return;
    }
    resolve(metadata);
  };
  video.onerror = () => {
    cleanup();
    reject(new Error("Could not read the processed camera clip."));
  };
  video.src = objectUrl;
});

const trimAndTranscodeCameraClip = async (
  { blob, contentType, trimStartMs },
  { loadEngine, readBlob, measureMetadata },
) => {
  const ffmpeg = await loadEngine();
  const token = nextFileToken();
  const inputName = `camera-input-${token}${extensionForContentType(contentType)}`;
  const outputName = `camera-output-${token}.mp4`;

  try {
    await ffmpeg.writeFile(inputName, await readBlob(blob));
    const exitCode = await ffmpeg.exec([
      "-i", inputName,
      "-ss", (trimStartMs / 1000).toFixed(3),
      "-map", "0:v:0",
      "-an",
      "-vf", "scale=trunc(iw/2)*2:trunc(ih/2)*2",
      "-c:v", "libx264",
      "-preset", "ultrafast",
      "-crf", "23",
      "-pix_fmt", "yuv420p",
      "-movflags", "+faststart",
      outputName,
    ]);
    if (exitCode !== 0) throw new Error("FFmpeg could not prepare the camera clip.");

    const output = await ffmpeg.readFile(outputName);
    const outputBlob = new Blob([output], { type: "video/mp4" });
    const metadata = await measureMetadata(outputBlob);
    return {
      blob: outputBlob,
      contentType: "video/mp4",
      durationMs: metadata.durationMs,
      width: metadata.width,
      height: metadata.height,
    };
  } finally {
    await ffmpeg.deleteFile(inputName).catch(() => {});
    await ffmpeg.deleteFile(outputName).catch(() => {});
  }
};

export const prepareCameraClip = async ({
  blob,
  contentType,
  durationMs,
  width,
  height,
  fps,
  trimStartMs,
  transcode,
  maxClipBytes,
  maxClipDurationMs,
}, dependencies = {}) => {
  const original = { blob, contentType, durationMs, width, height, fps };
  if (!transcode) {
    if (!cameraClipWithinLimits({ ...original, maxClipBytes, maxClipDurationMs })) {
      throw new Error("The camera clip exceeds its upload limits.");
    }
    return { ...original, trimmed: false, usedFallback: false, warning: null };
  }

  const processorDependencies = {
    loadEngine: loadFFmpeg,
    readBlob: fetchFile,
    measureMetadata: measureVideoMetadata,
    ...dependencies,
  };

  try {
    const processed = await trimAndTranscodeCameraClip(
      { blob, contentType, trimStartMs: Math.max(0, trimStartMs || 0) },
      processorDependencies,
    );
    const result = { ...original, ...processed };
    if (!cameraClipWithinLimits({ ...result, maxClipBytes, maxClipDurationMs })) {
      throw new Error("The processed camera clip exceeds its upload limits.");
    }
    return {
      ...result,
      trimmed: trimStartMs > 0,
      usedFallback: false,
      warning: null,
    };
  } catch (processingError) {
    if (cameraClipWithinLimits({ ...original, maxClipBytes, maxClipDurationMs })) {
      return {
        ...original,
        trimmed: false,
        usedFallback: true,
        warning: "The clip could not be shortened in this browser. The original valid recording will be uploaded.",
      };
    }
    throw processingError;
  }
};
